import { useTranslation } from '@dreamstack/i18n'
import { ChevronLeft } from '@dreamstack/icons/src/icon-components/ChevronLeft'
import { ChevronRight } from '@dreamstack/icons/src/icon-components/ChevronRight'
import { assertPopulatedDeep } from '@dreamstack/payload/src/lib/assertPopulated'
import { RouterOutputs } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util/src/cn'
import { normalizeHref } from '@dreamstack/util/src/normalizeHref'
import { Popover } from '@headlessui/react'
import { motion, useAnimationControls } from 'framer-motion'
import map from 'lodash/map'
import some from 'lodash/some'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { forwardRef, useEffect, useState } from 'react'
import { useNavbarDisplaySettings } from '../hooks/useNavbarDisplaySettings'
import { useProjectsSlug } from '../hooks/useProjectsSlug'
import { ExtraNavbarEntry } from './EntryWithSubNavbar'
import { MobileMotionPanel } from './MobileMotionPanel'
import {
  MobileNavEntriesWrapper,
  NavDivider,
  StyledMobileText,
  mobileFadeOutVariants,
} from './MobileNavBar'
// subnavActiveStyle previously: border-b-accentroWhite-full border-b-2
const StyledNavEntry = forwardRef<
  HTMLAnchorElement,
  React.PropsWithChildren<React.ComponentProps<'a'>>
>(({ className, children, ...rest }, ref) => {
  return (
    <a
      ref={ref}
      className={cn(
        'px-px-32 py-px-12 cursor-pointer block hover:bg-[rgba(255,255,255,0.025)]',
        className
      )}
      {...rest}
    >
      {children || <span className="sr-only">Navigation item</span>}
    </a>
  )
})

StyledNavEntry.displayName = 'StyledNavEntry'

const StyledMobileSubNavText = ({
  isActive,
  className,
  ...rest
}: {
  isActive?: boolean
  className?: string
} & React.ComponentProps<'span'>) => (
  <span
    className={cn(
      'ml:font-semibold inline-block text-px-16',
      isActive && 'border-b-accentroWhite-full border-b-2',
      className
    )}
    {...rest}
  />
)

// TODO: typings.
type MobileEntryWithSubNavbarProps = {
  controls: ReturnType<typeof useAnimationControls>
  entry: Pick<
    RouterOutputs['cms']['getNavEntries'][number],
    'id' | 'label' | 'staticPage' | 'subEntries' | 'contentfulId' | 'url'
  >
  mobileNavOpen: boolean
  setFadedOut: React.Dispatch<React.SetStateAction<boolean>>
}

export const MobileEntryWithSubNavbar = ({
  entry,
  setFadedOut,
  controls,
  mobileNavOpen,
}: MobileEntryWithSubNavbarProps) => {
  assertPopulatedDeep(entry)
  const { asPath } = useRouter()
  const t = useTranslation()
  const isActive =
    !!entry?.staticPage?.slug &&
    (asPath.endsWith(entry.staticPage.slug) ||
      some(entry?.subEntries, (item) => {
        return item?.staticPage?.slug && asPath.endsWith(item.staticPage.slug)
      }))

  const { displayDefaultSubEntry, displayProjectsSubEntry } =
    useNavbarDisplaySettings(entry)
  const projectsSlug = useProjectsSlug()
  const [openState, setOpenState] = useState<boolean>(false)

  useEffect(() => {
    if (!mobileNavOpen) setOpenState(false)
  }, [mobileNavOpen])
  const finalHref = normalizeHref({
    href: entry?.staticPage?.slug ? `/${entry?.staticPage?.slug}` : entry?.url,
  })
  return (
    <Popover className="focus-visible:outline-none">
      <div>
        <motion.div animate={controls} variants={mobileFadeOutVariants}>
          <button
            onClick={() => setOpenState(true)}
            className="font-semibold w-full flex justify-between focus-visible:outline-none focus:outline-none"
          >
            <StyledMobileText isActive={isActive}>
              {entry.label}
            </StyledMobileText>
            <ChevronRight className="w-px-24 h-px-24" />
          </button>
        </motion.div>
        <MobileMotionPanel
          setFadedOut={setFadedOut}
          entry={entry}
          open={openState}
          mobileNavOpen={mobileNavOpen}
        >
          <MobileNavEntriesWrapper>
            <div className="flex mb-px-16">
              <button
                onClick={() => setOpenState(false)}
                className="font-semibold flex items-center justify-between focus-visible:outline-none focus:outline-none"
              >
                <ChevronLeft className="h-px-24 w-px-24 mr-px-8" />
                <StyledMobileText>{entry.label}</StyledMobileText>
              </button>
            </div>
            <NavDivider />
            <div className="py-px-16">
              {displayDefaultSubEntry && (
                <Link href={finalHref} passHref legacyBehavior>
                  <StyledNavEntry className="group">
                    <StyledMobileSubNavText
                      isActive={
                        !!entry?.staticPage?.slug &&
                        asPath.endsWith(entry.staticPage.slug)
                      }
                    >
                      {t('accentro:navigation.overview')}
                    </StyledMobileSubNavText>
                  </StyledNavEntry>
                </Link>
              )}
              {map(entry?.subEntries, (subEntry) => {
                const isActive =
                  !!subEntry?.staticPage?.slug &&
                  asPath.endsWith(subEntry.staticPage.slug)
                const finalHref = normalizeHref({
                  href: subEntry?.staticPage?.slug
                    ? `/${subEntry?.staticPage?.slug}`
                    : subEntry?.url,
                })
                return (
                  <Link
                    key={subEntry?.id}
                    href={finalHref}
                    passHref
                    legacyBehavior
                  >
                    <StyledNavEntry className="group">
                      <StyledMobileSubNavText isActive={isActive}>
                        {subEntry?.label}
                      </StyledMobileSubNavText>
                    </StyledNavEntry>
                  </Link>
                )
              })}
              {displayProjectsSubEntry && (
                <ExtraNavbarEntry
                  slug={projectsSlug}
                  title={t('accentro:navigation.projects')}
                />
              )}
            </div>
          </MobileNavEntriesWrapper>
        </MobileMotionPanel>
      </div>
    </Popover>
  )
}
